import { EmissionQualifyingParams } from '@/shared/api/EmissionQualifyingParamsModel'

import {
  SelectGroup,
  isProductionMethodAluminium,
  isProductionMethodIronAndSteel,
  ProductionMethods,
  Card,
  Button,
  CardHeader
} from '@/common'
import {
  AluminiumEmissionQualifyingParams,
  CalcinedClayEmissionQualifyingParams,
  CementEmissionQualifyingParams,
  IronAndSteelEmissionQualifyingParams
} from '@/shared/api/EmissionQualifyingParamsModel'

import AluminiumQualifyingParams from './qualifyingParams/AluminiumQualifyingParamsForm'
import CementQualifyingParamsForm from './qualifyingParams/CementQualifyingParamsForm'
import CalcinedClayQualifyingParamsForm from './qualifyingParams/CalcinedClayQualifyingParamsForm'
import IronAndSteelQualifyingParamsForm from './qualifyingParams/IronAndSteelQualifyingParamsForm'
import { useState } from 'react'
import { CheckIcon } from '@/common/components/icons'
import {
  DataQualityInfo,
  JustificationInfo,
  QualityAssuranceInfo,
  dataQualityOptions,
  justificationOptions,
  qualityAssuranceOptions
} from '@/features/dashboard/pages/finalized-reports/finalized-report-page/constants'
import { GroupedMultiSelect } from '@/features/dashboard/pages/reports/pages/reports-page/componets/GroupedMultiSelect/GroupedMultiSelect'
import useI18n from '@/shared/hooks/useI18n'

type State = {
  dataQualityInfo: DataQualityInfo | null
  qualityAssuranceInfo: QualityAssuranceInfo | null
  defaultValuesJustification: JustificationInfo[] | null
}

export default function FinalizedReportDialogBody({
  productionMethodCodes,
  state,
  qualifyingParams,
  setQualifyingParams,
  updateState,
  requiredQualifyingParamsCodes
}: {
  productionMethodCodes: string[]
  state: State
  qualifyingParams: EmissionQualifyingParams
  setQualifyingParams: (params: EmissionQualifyingParams) => void
  updateState: (state: State) => void
  requiredQualifyingParamsCodes: string[]
}) {
  const { t } = useI18n()
  const { dataQualityInfo, qualityAssuranceInfo, defaultValuesJustification } = state

  const [expandedJustification, setExpandedJustification] = useState<JustificationInfo[]>([])

  const isTheFinalizedReportOnlyForAluminium = productionMethodCodes.every(
    isProductionMethodAluminium
  )

  const isTheFinalizedReportOnlyForIronAndSteel = productionMethodCodes.every(
    isProductionMethodIronAndSteel
  )

  const isCement = productionMethodCodes.includes(ProductionMethods.Cement)
  const isCalcinedClay = productionMethodCodes.includes(ProductionMethods.CalcinedClay)

  const onChangeDataQualityInfo = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = dataQualityOptions.find(opt => opt.value === event.target.value)
    updateState({ ...state, dataQualityInfo: selected ? selected.value : null })
  }

  const onChangeQualityAssuranceInfo = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = qualityAssuranceOptions.find(opt => opt.value === event.target.value)
    updateState({ ...state, qualityAssuranceInfo: selected ? selected.value : null })
  }

  const toggleJustification = (val: JustificationInfo) => {
    setExpandedJustification(prev =>
      prev.includes(val) ? prev.filter(value => value !== val) : [...prev, val]
    )
  }

  const SelectInfoSection = ({
    labelKey,
    tooltipKey,
    name,
    selectedValue,
    options,
    onChange
  }: {
    labelKey: string
    tooltipKey: string
    name: string
    selectedValue: string | null
    options: { value: string; labelKey: string; infoKey: string }[]
    onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void
  }) => {
    const [expanded, setExpanded] = useState(false)

    const mappedOptions = options.map(option => ({
      label: t(option.labelKey),
      value: option.value
    }))

    const selectedOption = options.find(option => option.value === selectedValue)

    const toggle = () => setExpanded(prev => !prev)

    return (
      <div className="space-y-2">
        <SelectGroup
          label={t(labelKey)}
          tooltip={t(tooltipKey)}
          name={name}
          value={selectedOption?.value || ''}
          onChange={event => {
            setExpanded(false)
            onChange(event)
          }}
          options={mappedOptions}
        />

        {selectedValue && !expanded && (
          <Card>
            <div className="flex items-center gap-2 justify-between p-4">
              <p className="flex items-center gap-2 font-medium text-sm">
                <CheckIcon className="size-4" />
                {selectedOption ? t(selectedOption.labelKey) : ''}
              </p>
              <Button variant="txt-default" className="justify-end" onClick={toggle}>
                {t('reports:finalizedReports.fields.viewAddedClause')}
              </Button>
            </div>
          </Card>
        )}

        {selectedValue && expanded && (
          <Card variant="subtle">
            <CardHeader className="!p-4">
              <div className="flex items-center gap-2 justify-between">
                <p className="flex items-center gap-2 font-medium text-sm">
                  <CheckIcon className="size-4" />
                  {selectedOption ? t(selectedOption.labelKey) : ''}
                </p>
                <Button variant="txt-default" className="justify-end" onClick={toggle}>
                  {t('reports:finalizedReports.fields.hideAddedClause')}
                </Button>
              </div>
            </CardHeader>
            <div className="p-4">
              <p className="text-sm text-text-secondary">
                {selectedOption ? t(selectedOption.infoKey) : ''}
              </p>
            </div>
          </Card>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4 mb-6">
      <div className="space-y-2">
        <h3 className="font-semibold">
          {t(
            'reports:finalizedReports.createFinalizeReportDialog.sections.dataQualityAndAssurance.title'
          )}
        </h3>
        <GroupedMultiSelect
          grouping
          items={justificationOptions}
          tooltip={t('reports:finalizedReports.fields.justificationInfo.tooltip')}
          label={t('reports:finalizedReports.fields.justificationInfo.label')}
          placeholder={t('reports:finalizedReports.fields.justificationInfo.placeholder')}
          selectedValues={defaultValuesJustification ?? []}
          onChange={selectedValues => {
            updateState({
              ...state,
              defaultValuesJustification: selectedValues as JustificationInfo[]
            })
          }}
        />
        {defaultValuesJustification && defaultValuesJustification.length > 0 && (
          <div className="space-y-2">
            {defaultValuesJustification.map((info: JustificationInfo) => {
              const expanded = expandedJustification.includes(info)
              const option = justificationOptions.find(option => option.value === info)
              if (!option) {return null}
              return (
                <Card key={info} variant={expanded ? 'subtle' : 'default'}>
                  <CardHeader className={`!p-4 ${expanded ? '' : '!bg-white'}`}>
                    <div className="flex items-center gap-2 justify-between">
                      <p className="flex items-center gap-2 font-medium text-sm">
                        <CheckIcon className="size-4" />
                        {t(option.labelKey)}
                      </p>
                      <Button
                        variant="txt-default"
                        className="justify-end"
                        onClick={() => toggleJustification(info)}
                      >
                        {expanded
                          ? t('reports:finalizedReports.fields.hideAddedClause')
                          : t('reports:finalizedReports.fields.viewAddedClause')}
                      </Button>
                    </div>
                  </CardHeader>
                  {expanded && (
                    <div className="p-4">
                      <p className="text-sm text-text-secondary">{t(option.infoKey)}</p>
                    </div>
                  )}
                </Card>
              )
            })}
          </div>
        )}
      </div>

      <SelectInfoSection
        labelKey="reports:finalizedReports.fields.dataQualityInfo.label"
        tooltipKey="reports:finalizedReports.createFinalizeReportDialog.tooltips.dataQualityInfo"
        name="dataQualityInfo"
        selectedValue={dataQualityInfo}
        options={dataQualityOptions}
        onChange={onChangeDataQualityInfo}
      />

      <SelectInfoSection
        labelKey="reports:finalizedReports.fields.qualityAssuranceInfo.label"
        tooltipKey="reports:finalizedReports.createFinalizeReportDialog.tooltips.qualityAssuranceInfo"
        name="qualityAssuranceInfo"
        selectedValue={qualityAssuranceInfo}
        options={qualityAssuranceOptions}
        onChange={onChangeQualityAssuranceInfo}
      />
      {Object.keys(qualifyingParams).length > 0 && (
        <div className="space-y-4">
          <h3 className="font-semibold">
            {t(
              'reports:finalizedReports.createFinalizeReportDialog.sections.qualifyingParameters.title'
            )}
          </h3>
          {isTheFinalizedReportOnlyForAluminium && (
            <AluminiumQualifyingParams
              qualifyingParams={qualifyingParams as AluminiumEmissionQualifyingParams}
              setQualifyingParams={setQualifyingParams}
            />
          )}
          {isCement && (
            <CementQualifyingParamsForm
              qualifyingParams={qualifyingParams as CementEmissionQualifyingParams}
              setQualifyingParams={setQualifyingParams}
            />
          )}
          {isCalcinedClay && (
            <CalcinedClayQualifyingParamsForm
              qualifyingParams={qualifyingParams as CalcinedClayEmissionQualifyingParams}
              setQualifyingParams={setQualifyingParams}
            />
          )}
          {isTheFinalizedReportOnlyForIronAndSteel && (
            <IronAndSteelQualifyingParamsForm
              requiredQualifyingParamsCodes={requiredQualifyingParamsCodes}
              productionMethodCodes={productionMethodCodes}
              qualifyingParams={qualifyingParams as IronAndSteelEmissionQualifyingParams}
              setQualifyingParams={setQualifyingParams}
            />
          )}
        </div>
      )}
    </div>
  )
}
